import React, { FC, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import ContentBaseLayout from '../../../libx/shared/ContentBaseLayout';
import { ProductGroupContainer } from './ProductGroupContainer';
import { useLibraryManagedProductGroupsFeaturePages } from '../api/useLibraryManagedProductGroupsFeaturePages';
import { invalidateFeaturePageProducts, useFeaturePageProducts } from '../api/useFeaturePageProducts';
import { useReorderProductGroups } from './useReorderProductGroups';
import { calculateTopValues, PRODUCT_GROUP_MAX_HEIGHT } from './utils/calculate-top-values';
import { mapProductGroups } from './utils/map-product-groups';
import { usePreviousProps } from '../../../../hooks/usePreviousProps';
import NoElementsFound, { NO_ELEMENTS_PRODUCT_GROUPS } from '../../../../shared/components/NoElementsFound';
import { useTranslation } from 'react-i18next';
import { useAddToList } from '../../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../../shared/common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../../../../shared/common/UnavailableProductsModal';
import { useSearchParams } from 'react-router-dom';

const StProductGroupWrapper = styled.div<{ height: number; isLoading: boolean }>`
    position: relative;
    height: ${props => props.height}px;
    min-height: ${props => (props.isLoading ? '100vh' : '')};
`;

const StProductGroupContainer = styled(ProductGroupContainer)<{ top: string }>`
    position: absolute;
    top: ${props => props.top};
    left: 0;
    right: 0;
`;

export const ProductGroupOverview: FC = () => {
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const libScope = searchParams.get('lib-scope');
    const catalogueManagementScopeId = libScope ? parseInt(libScope) : 1;
    const previousCatalogueManagementScopeId = usePreviousProps(catalogueManagementScopeId);

    const { data: featuredPages, isLoading: isFeaturedPagesLoading } = useLibraryManagedProductGroupsFeaturePages();

    const featuredPage = featuredPages?.find(page => page.scopeId === catalogueManagementScopeId);
    const productGroupIds =
        featuredPage?.productGroupSlots?.flatMap(({ productGroup }) => (productGroup?.id ? [productGroup.id] : [])) ??
        [];

    const { data: products, isLoading: isProductGroupsLoading } = useFeaturePageProducts({
        featurePageId: featuredPage?.id,
        productGroupIds
    });

    const productGroups = useMemo(
        () => mapProductGroups(language, featuredPage, products),
        [featuredPage, language, products]
    );

    const indexToTopValue = calculateTopValues(productGroups);

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => invalidateFeaturePageProducts().then() });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    useEffect(() => {
        if (catalogueManagementScopeId !== previousCatalogueManagementScopeId) {
            setSearchParams(
                current => {
                    current.set('lib-scope', catalogueManagementScopeId.toString());
                    return current;
                },
                { replace: true }
            );
        }
    }, [catalogueManagementScopeId, previousCatalogueManagementScopeId, setSearchParams]);

    const { moveDown, moveUp, isSavingOrderLoading } = useReorderProductGroups(productGroups);

    const isLoading = isProductGroupsLoading || isFeaturedPagesLoading || isSavingOrderLoading;

    return (
        <>
            <ContentBaseLayout
                loading={isLoading}
                documentTitle={t('shop.productGroups.titleOverview')}
                contentComponent={
                    <StProductGroupWrapper height={indexToTopValue.wrapperHeight} isLoading={isLoading}>
                        {productGroups.map((productGroup, index) => (
                            <StProductGroupContainer
                                key={productGroup.id}
                                top={indexToTopValue[index].top}
                                libraryManagedSlotNumber={index}
                                moveDown={moveDown}
                                moveUp={moveUp}
                                active={productGroup.active}
                                usageStatistics={productGroup.usageStatistics}
                                productGroupId={productGroup.productGroupId}
                                title={productGroup.title}
                                productGroupSliderHeight={indexToTopValue[index].height}
                                productGroupSliderHeightHover={PRODUCT_GROUP_MAX_HEIGHT}
                                sliderProducts={productGroup.products ?? []}
                                numberOfProductGroupsInScope={productGroups?.length}
                                lastEditedEpochMillis={productGroup.lastEditedEpochMillis}
                                onAddPrice={changePrice}
                            />
                        ))}
                        {!isLoading && productGroups.length === 0 ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCT_GROUPS} />
                        ) : null}
                    </StProductGroupWrapper>
                }
            />

            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
